export interface CompressResult {
  isCompressed: boolean;
  file: File;
  originalSize: number;
  compressedSize: number;
  ratio: string;
}

// 2026-08-27 改造核心保證：無論瀏覽器能力如何，輸出不得超過此上限
//（根因：Safari canvas 不支援 WebP 編碼會靜默回退 PNG，PNG 無法縮小 → 曾產出 2.2MB 假 webp）
const HARD_CAP_BYTES = 300 * 1024;
const MIN_QUALITY = 0.3;
const MIN_WIDTH = 600;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getInitialSettings(fileSizeKB: number) {
  if (fileSizeKB > 3072) return { maxWidth: 1200, quality: 0.4 };
  if (fileSizeKB > 1024) return { maxWidth: 1400, quality: 0.5 };
  return { maxWidth: 1600, quality: 0.6 };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

/**
 * 格式自適應編碼：優先 WebP；若瀏覽器（Safari 系）不支援而靜默回退 PNG，
 * 改用 JPEG 編碼。回傳實際 Blob 與正確副檔名——絕不產生「副檔名與內容不符」的檔案。
 */
async function encodeCanvas(
  canvas: HTMLCanvasElement,
  quality: number
): Promise<{ blob: Blob; ext: 'webp' | 'jpg' } | null> {
  const webp = await canvasToBlob(canvas, 'image/webp', quality);
  if (webp && webp.type === 'image/webp') return { blob: webp, ext: 'webp' };
  const jpg = await canvasToBlob(canvas, 'image/jpeg', quality);
  if (jpg && jpg.type === 'image/jpeg') return { blob: jpg, ext: 'jpg' };
  return null;
}

async function tryCompress(
  img: HTMLImageElement,
  maxWidth: number,
  quality: number
): Promise<{ file: File; size: number } | null> {
  let { width, height } = img;

  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(img, 0, 0, width, height);

  const encoded = await encodeCanvas(canvas, quality);
  if (!encoded) return null;

  const originalName = 'image'; // 呼叫端以壓縮結果命名
  const file = new File([encoded.blob], `${originalName}.${encoded.ext}`, {
    type: encoded.blob.type,
    lastModified: Date.now(),
  });
  return { file, size: encoded.blob.size };
}

/**
 * 智慧型階梯式圖片壓縮（2026-08-27 強化版）
 * - 階梯壓縮（maxWidth/品質遞減，最多 6 次）
 * - 終極保證：輸出超過 300KB 時持續降解析度＋品質，直到 ≤300KB（底線 600px 寬）
 * - 格式自適應：WebP 不可用自動改 JPEG，不再產生「假 webp 實 PNG」的巨型檔
 * - ≤ 60KB / SVG / GIF：不壓縮，保留原檔（60KB 以下無論格式都夠小）
 */
export async function smartCompressImage(file: File): Promise<CompressResult> {
  if (file.type === 'image/svg+xml' || file.type === 'image/gif' || file.size <= 60 * 1024) {
    return {
      isCompressed: false,
      file,
      originalSize: file.size,
      compressedSize: file.size,
      ratio: '0%',
    };
  }

  const fileSizeKB = file.size / 1024;
  const initial = getInitialSettings(fileSizeKB);

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    const objectUrl = URL.createObjectURL(file);
    el.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(el);
    };
    el.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('圖片載入失敗'));
    };
    el.src = objectUrl;
  });

  let maxWidth = initial.maxWidth;
  let quality = initial.quality;
  let best: { file: File; size: number } | null = null;

  for (let attempt = 0; attempt < 6; attempt++) {
    best = await tryCompress(img, maxWidth, quality);
    if (best && best.size <= HARD_CAP_BYTES) break;
    maxWidth = Math.round(maxWidth * 0.8);
    quality = Math.max(MIN_QUALITY, quality - 0.1);
    if (maxWidth < MIN_WIDTH) break;
  }

  // 終極保證：仍超標就持續降寬度重編（品質固定低檔），直到 ≤300KB 或到 600px 底線
  while (best && best.size > HARD_CAP_BYTES && maxWidth > MIN_WIDTH) {
    maxWidth = Math.max(MIN_WIDTH, Math.round(maxWidth * 0.8));
    best = await tryCompress(img, maxWidth, MIN_QUALITY);
  }

  if (!best) {
    // 完全壓不出來（罕見）：僅在原檔本來就低於上限時放行
    if (file.size <= HARD_CAP_BYTES) {
      return { isCompressed: false, file, originalSize: file.size, compressedSize: file.size, ratio: '0%' };
    }
    throw new Error('圖片壓縮失敗：請改用 JPG/PNG 格式或較小的相片再試');
  }

  const ratio = (((file.size - best.size) / file.size) * 100).toFixed(0);
  const finalName = `${file.name.replace(/\.[^.]+$/, '')}_compressed.${best.file.type.split('/')[1]}`;

  return {
    isCompressed: true,
    file: new File([best.file], finalName, { type: best.file.type, lastModified: Date.now() }),
    originalSize: file.size,
    compressedSize: best.size,
    ratio: `${ratio}%`,
  };
}

export { formatSize };
